// src/components/Login.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAppVersionLabel } from "../config/appVersion";
import { httpsCallable } from "firebase/functions";
import {
  confirmPasswordReset,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
} from "firebase/auth";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { functions, auth, db } from "../firebase";
import toast from "react-hot-toast";
import WinsAutosLogo from "./WinsAutosLogo";

const REMEMBER_DEVICE_KEY = "loginRememberDevice";
const REMEMBERED_USER_KEY = "loginRememberedUser";

/** Normalize Firebase Functions / Auth error codes for checks */
function errorCode(err) {
  return String(err?.code || err?.errorInfo?.code || "").toLowerCase();
}

function errorDetailMessage(err) {
  const d = err?.details;
  if (typeof d === "string" && d.trim()) return d;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  return "";
}

export default function Login() {
  const navigate = useNavigate();
  const [remember, setRemember] = useState(
    () => localStorage.getItem(REMEMBER_DEVICE_KEY) === "1"
  );
  const [username, setUsername] = useState(() =>
    localStorage.getItem(REMEMBER_DEVICE_KEY) === "1"
      ? localStorage.getItem(REMEMBERED_USER_KEY) || ""
      : ""
  );
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [userFocused, setUserFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  /** null | loading | form | expired | done */
  const [resetFlow, setResetFlow] = useState(null);
  const [oobCode, setOobCode] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const appVersion = getAppVersionLabel();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const code = params.get("oobCode");
    if (mode !== "resetPassword" || !code) return;

    setResetFlow("loading");
    setOobCode(code);
    setError("");
    setResetMessage("");

    verifyPasswordResetCode(auth, code)
      .then((email) => {
        setResetEmail(email);
        setResetFlow("form");
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((err) => {
        console.error("Reset link validation failed:", err);
        setResetFlow("expired");
        window.history.replaceState({}, "", window.location.pathname);
      });
  }, []);

  const persistRememberChoice = (u) => {
    if (remember) {
      localStorage.setItem(REMEMBER_DEVICE_KEY, "1");
      localStorage.setItem(REMEMBERED_USER_KEY, u);
    } else {
      localStorage.removeItem(REMEMBER_DEVICE_KEY);
      localStorage.removeItem(REMEMBERED_USER_KEY);
    }
  };

  const doNavigate = (role, permissions, usernameForStorage, branchOpts = {}) => {
    const u = usernameForStorage != null ? usernameForStorage : username.trim();
    persistRememberChoice(u);
    const roleNorm = String(role || "staff").toLowerCase();
    const payload = {
      uid: auth.currentUser?.uid,
      username: u,
      role: roleNorm === "administrator" ? "admin" : roleNorm,
    };
    if (Array.isArray(permissions)) {
      payload.permissions = permissions;
    }
    if (Array.isArray(branchOpts.allowedBranches)) {
      payload.allowedBranches = branchOpts.allowedBranches;
    }
    localStorage.setItem("user", JSON.stringify(payload));
    navigate("/dashboard", { replace: true });
  };

  const resolveLoginEmailFn = httpsCallable(functions, "resolveLoginEmail");
  const requestPasswordResetFn = httpsCallable(functions, "requestPasswordReset");

  const handleForgotPassword = async () => {
    setError("");
    setResetMessage("");

    const id = username.trim();
    if (!id) {
      setError("Enter your username or email above, then click Forgot Password.");
      return;
    }

    setResetSending(true);
    try {
      const continueUrl = `${window.location.origin}/login`;
      const res = await requestPasswordResetFn({ username: id, continueUrl });
      const email = res?.data?.email || id;
      const delivery = res?.data?.delivery;
      const deliveryNote =
        delivery === "smtp"
          ? " Sent from your company mail server."
          : delivery === "firebase"
            ? " Sent via Firebase (check spam if not in inbox)."
            : "";

      setResetMessage(
        `Password reset email sent to ${email}.${deliveryNote} Use the link in the newest email only (older links stop working). The link opens this page to set a new password.`
      );
      toast.success("Password reset email sent");
    } catch (err) {
      console.error("Password reset failed:", err);
      const c = errorCode(err);
      const details = errorDetailMessage(err);

      if (c.includes("not-found")) {
        setError("No account found for that username or email.");
      } else if (c.includes("failed-precondition")) {
        setError(details || "This account cannot receive a reset email. Contact your administrator.");
      } else if (c.includes("invalid-argument")) {
        setError("Enter your username or email above, then click Forgot Password.");
      } else if (c.includes("too-many-requests")) {
        setError("Too many attempts. Wait a few minutes and try again.");
      } else if (details) {
        setError(details);
      } else {
        setError(
          String(err?.message || "Could not send reset email.")
            .replace(/^Firebase:\s*/i, "")
            .replace(/\(.*\)\s*$/, "")
            .trim() ||
            "Could not send reset email. Deploy Cloud Functions (requestPasswordReset) or contact your administrator."
        );
      }
    } finally {
      setResetSending(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setResetSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setNewPassword("");
      setConfirmPassword("");
      setOobCode("");
      setResetEmail("");
      setResetFlow(null);
      if (resetEmail) setUsername(resetEmail);
      setResetMessage("Password updated. Sign in with your email and new password.");
      toast.success("Password reset successfully");
    } catch (err) {
      console.error("confirmPasswordReset failed:", err);
      const c = errorCode(err);
      if (c.includes("expired-action-code") || c.includes("invalid-action-code")) {
        setResetFlow("expired");
      } else if (c.includes("weak-password")) {
        setError("Password is too weak. Use at least 6 characters.");
      } else {
        setError(
          String(err?.message || "Could not reset password.")
            .replace(/^Firebase:\s*/i, "")
            .replace(/\(.*\)\s*$/, "")
            .trim() || "Could not reset password. Request a new link and try again."
        );
      }
    } finally {
      setResetSubmitting(false);
    }
  };

  const exitResetFlow = () => {
    setResetFlow(null);
    setOobCode("");
    setResetEmail("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  /** Resolve user row for login id. Pre-auth uses Cloud Function (Firestore users is not public). */
  const findUserRowByLoginId = async (id) => {
    if (!id) return null;

    if (auth.currentUser) {
      let q = query(collection(db, "users"), where("username", "==", id), limit(1));
      let snap = await getDocs(q);
      if (snap.empty) {
        q = query(collection(db, "users"), where("usernameLower", "==", id.toLowerCase()), limit(1));
        snap = await getDocs(q);
      }
      if (snap.empty && id.includes("@")) {
        q = query(collection(db, "users"), where("email", "==", id.toLowerCase()), limit(1));
        snap = await getDocs(q);
      }
      if (snap.empty) return null;
      return { id: snap.docs[0].id, data: snap.docs[0].data() || {} };
    }

    try {
      const res = await resolveLoginEmailFn({ username: id });
      const d = res?.data || {};
      if (!d.email) return null;
      return {
        id: d.id || "",
        data: {
          email: d.email,
          username: d.username || id,
          role: d.role,
          permissions: d.permissions,
          allowedBranches: d.allowedBranches,
        },
      };
    } catch (e) {
      const c = errorCode(e);
      if (c.includes("not-found") || c.includes("failed-precondition")) return null;
      throw e;
    }
  };

  /**
   * When the login field looks like an email, try Firebase email/password first.
   * This matches passwords set via reset link (Firebase) even if Firestore bcrypt differs.
   */
  const tryFirebaseEmailSignIn = async () => {
    const id = username.trim();
    if (!id.includes("@")) return false;
    try {
      await signInWithEmailAndPassword(auth, id.toLowerCase(), password);
      await auth.currentUser.getIdToken(true);
      const tr = await auth.currentUser.getIdTokenResult();

      let role = tr.claims.role;
      let perms = tr.claims.permissions;
      let displayName = id;
      let allowedBranches = Array.isArray(tr.claims.allowedBranches) ? tr.claims.allowedBranches : undefined;

      try {
        const row = await findUserRowByLoginId(id);
        if (row?.data) {
          const d = row.data;
          role = d.role || role || "staff";
          perms = Array.isArray(d.permissions) ? d.permissions : perms;
          if (d.username) displayName = d.username;
          if (Array.isArray(d.allowedBranches)) allowedBranches = d.allowedBranches;
        }
      } catch (e) {
        console.warn("Firestore user row not readable; using token claims only", e);
      }

      doNavigate(role || "viewer", perms, displayName, { allowedBranches });
      return true;
    } catch (e) {
      const c = errorCode(e);
      if (c.includes("wrong-password") || c.includes("invalid-credential") || c.includes("user-not-found")) {
        return false;
      }
      if (c.includes("user-disabled")) {
        throw e;
      }
      console.warn("Email sign-in unexpected error", e);
      return false;
    }
  };

  const tryEmailPasswordFallback = async () => {
    const id = username.trim();
    if (!id) return false;

    const row = await findUserRowByLoginId(id);
    if (!row) return false;

    const userData = row.data;
    const email = userData.email;
    if (!email) return false;

    try {
      await signInWithEmailAndPassword(auth, email, password);
      await auth.currentUser.getIdToken(true);
      const tr = await auth.currentUser.getIdTokenResult();
      const fromClaims = tr.claims.permissions;
      const fromClaimsBranches = Array.isArray(tr.claims.allowedBranches)
        ? tr.claims.allowedBranches
        : undefined;
      const ab =
        Array.isArray(userData.allowedBranches) ? userData.allowedBranches : fromClaimsBranches;
      const perms = Array.isArray(userData.permissions)
        ? userData.permissions
        : Array.isArray(fromClaims)
          ? fromClaims
          : undefined;
      doNavigate(userData.role || "staff", perms, userData.username || id, { allowedBranches: ab });
      return true;
    } catch (fallbackErr) {
      const c = errorCode(fallbackErr);
      if (c.includes("user-disabled")) throw fallbackErr;
      console.error("Email/password fallback failed:", fallbackErr);
      return false;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResetMessage("");
    setLoading(true);

    const id = username.trim();
    const pwd = password;
    /** Avoid running the same Firestore+email sign-in twice on failure (username path runs it before verifyLogin). */
    let skipEmailFallbackInCatch = false;

    try {
      // 1) Email-shaped login → Firebase Auth password (reset link / Auth-only accounts)
      if (id.includes("@")) {
        const okEmail = await tryFirebaseEmailSignIn();
        if (okEmail) return;
      }

      // 2) Username login → try Auth email+password first (often matches after email reset while Firestore hash is stale)
      if (!id.includes("@")) {
        const okByEmail = await tryEmailPasswordFallback();
        if (okByEmail) return;
        skipEmailFallbackInCatch = true;
      }

      // 3) Callable: verify password against Firestore hash + issue custom token
      const verify = httpsCallable(functions, "verifyLogin");
      const res = await verify({ username: id, password: pwd });

      const token = res?.data?.token;
      if (!token) throw new Error("No token returned");

      await signInWithCustomToken(auth, token);
      await auth.currentUser.getIdToken(true);
      const idTokenResult = await auth.currentUser.getIdTokenResult();

      if (idTokenResult.claims.mustChangePassword) {
        toast(
          "An administrator asked you to set a new password. Use a password reset link from your admin, or Firebase email recovery if enabled.",
          { duration: 8000 }
        );
      }

      const cp = idTokenResult.claims.permissions;
      const cab = idTokenResult.claims.allowedBranches;
      const dataAb = res?.data?.allowedBranches;
      const allowedBranches = Array.isArray(cab) ? cab : Array.isArray(dataAb) ? dataAb : undefined;
      doNavigate(
        idTokenResult.claims.role || "staff",
        Array.isArray(cp) ? cp : res?.data?.permissions,
        idTokenResult.claims.username || id,
        { allowedBranches }
      );
    } catch (err) {
      console.error("Login failed:", err);

      const code = errorCode(err);
      const details = errorDetailMessage(err);

      if (code.includes("user-disabled")) {
        setError(
          "This account is disabled in Firebase Authentication. Ask an administrator to enable it in the console."
        );
        return;
      }

      const tryFallback =
        code.includes("permission-denied") ||
        code.includes("internal") ||
        code.includes("already-exists") ||
        code.includes("not-found") ||
        code.includes("failed-precondition") ||
        code.includes("unavailable") ||
        code.includes("deadline-exceeded");

      if (tryFallback && !skipEmailFallbackInCatch) {
        try {
          const ok = await tryEmailPasswordFallback();
          if (ok) return;
        } catch (fallbackErr) {
          const fc = errorCode(fallbackErr);
          if (!fc.includes("permission-denied")) {
            console.error("Fallback chain error:", fallbackErr);
          }
        }
      }

      if (code.includes("not-found")) {
        setError("No account matches that username or email. Check spelling and spaces.");
      } else if (code.includes("failed-precondition")) {
        const msg = details || err?.message || "";
        if (/disabled/i.test(msg)) {
          setError(
            msg ||
              "This account is disabled in Firebase Authentication. Ask an administrator to enable it in the console."
          );
        } else {
          setError(
            msg ||
              "Password is not set for this user. Ask an admin to set a password or use Invite / reset link."
          );
        }
      } else if (code.includes("permission-denied")) {
        setError(details && details !== "Invalid password" ? details : "Incorrect password.");
      } else if (code.includes("invalid-argument")) {
        setError("Enter both username (or email) and password.");
      } else if (code.includes("already-exists")) {
        setError("Account mapping conflict. Contact admin.");
      } else if (String(err).toLowerCase().includes("cors")) {
        setError("Cannot reach login service. Deploy Cloud Functions (verifyLogin) and use region us-central1.");
      } else if (details) {
        setError(details);
      } else if (err?.message) {
        setError(String(err.message).replace(/^Firebase:\s*/i, "").replace(/\(.*\)\s*$/, "").trim());
      } else {
        setError("Login failed. Confirm password with your admin or use email + password if you reset via email.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen flex min-h-screen flex-col bg-[#F8FAFC] text-prs-on-surface antialiased">
      <header className="flex items-center justify-between border-b border-prs-outline-variant/60 bg-white px-4 py-2.5 shadow-sm md:px-8">
        <div className="flex items-center gap-1.5">
          <WinsAutosLogo size={24} />
          <span className="text-xs font-bold text-prs-primary md:text-sm">{appVersion}</span>
        </div>
        <button
          type="button"
          className="rounded-full p-1.5 text-prs-on-surface-variant transition-colors hover:bg-prs-surface-low"
          title="Help"
          aria-label="Help"
        >
          <span className="material-symbols-outlined text-[18px]">help_outline</span>
        </button>
      </header>

      <main className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-4 py-6">
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <div className="absolute right-[-10%] top-[-10%] h-[500px] w-[500px] rounded-full bg-prs-secondary-container blur-[120px]" />
          <div className="absolute bottom-[-10%] left-[-10%] h-[400px] w-[400px] rounded-full bg-prs-primary-fixed blur-[100px]" />
        </div>

        <div className="z-10 w-full max-w-[380px]">
          <div className="rounded-lg border border-prs-outline-variant/30 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.05)]">
            <div className="space-y-4 p-5">
              <div className="space-y-1 text-center">
                <div className="mb-1 flex justify-center">
                  <WinsAutosLogo size={36} />
                </div>
                <h1 className="text-xl font-semibold leading-7 tracking-tight text-prs-primary">
                  {resetFlow === "form" || resetFlow === "loading"
                    ? "Set new password"
                    : resetFlow === "expired"
                      ? "Reset link expired"
                      : "Welcome back"}
                </h1>
                <p className="text-xs text-prs-on-surface-variant">
                  {resetFlow === "form"
                    ? resetEmail
                      ? `Create a new password for ${resetEmail}`
                      : "Create a new password for your account"
                    : resetFlow === "loading"
                      ? "Checking your reset link…"
                      : resetFlow === "expired"
                        ? "Request a new link below, then use only the newest email"
                        : "Sign in to your account to continue"}
                </p>
              </div>

              {resetFlow === "loading" && (
                <div className="flex justify-center py-6">
                  <span className="material-symbols-outlined animate-spin text-[28px] text-prs-primary">
                    progress_activity
                  </span>
                </div>
              )}

              {resetFlow === "expired" && (
                <div className="space-y-3.5">
                  <div
                    role="alert"
                    className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900"
                  >
                    <span className="material-symbols-outlined mt-0.5 shrink-0 text-[16px]">link_off</span>
                    <span>
                      This reset link has expired or was already used. Password reset links work once and
                      expire after about an hour. If you requested more than one email, only the latest link
                      works.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={exitResetFlow}
                    className="flex w-full items-center justify-center gap-1.5 rounded-md border border-prs-outline-variant bg-white py-2.5 text-xs font-medium text-prs-primary transition-colors hover:bg-prs-surface-low"
                  >
                    Back to sign in
                  </button>
                </div>
              )}

              {resetFlow === "form" && (
                <form onSubmit={handleResetPasswordSubmit} className="space-y-3.5">
                  <div className="space-y-0.5">
                    <label htmlFor="reset-new-password" className="text-xs font-medium text-prs-secondary">
                      New password
                    </label>
                    <div className="relative">
                      <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[17px] text-prs-outline">
                        lock
                      </span>
                      <input
                        id="reset-new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        type="password"
                        placeholder="••••••••"
                        className="login-input"
                        required
                        minLength={6}
                        autoComplete="new-password"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    <label htmlFor="reset-confirm-password" className="text-xs font-medium text-prs-secondary">
                      Confirm password
                    </label>
                    <div className="relative">
                      <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[17px] text-prs-outline">
                        lock
                      </span>
                      <input
                        id="reset-confirm-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        type="password"
                        placeholder="••••••••"
                        className="login-input"
                        required
                        minLength={6}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  {error && (
                    <div
                      role="alert"
                      className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-800"
                    >
                      <span className="material-symbols-outlined mt-0.5 shrink-0 text-[16px]">error</span>
                      <span>{error}</span>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={resetSubmitting}
                    className="flex w-full items-center justify-center gap-1.5 rounded-md bg-prs-primary-container py-2.5 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:bg-prs-primary active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {resetSubmitting ? (
                      <>
                        <span className="material-symbols-outlined animate-spin text-[16px]">
                          progress_activity
                        </span>
                        Updating…
                      </>
                    ) : (
                      "Update password"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={exitResetFlow}
                    className="w-full text-center text-xs font-medium text-prs-primary hover:underline"
                  >
                    Back to sign in
                  </button>
                </form>
              )}

              {!resetFlow && (
              <>
              <form onSubmit={onSubmit} className="space-y-3.5">
                <div className="space-y-0.5">
                  <label htmlFor="login-username" className="text-xs font-medium text-prs-secondary">
                    Email Address
                  </label>
                  <div className="relative">
                    <span
                      className={`material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[17px] transition-colors ${
                        userFocused ? "text-prs-primary" : "text-prs-outline"
                      }`}
                    >
                      mail
                    </span>
                    <input
                      id="login-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onFocus={() => setUserFocused(true)}
                      onBlur={() => setUserFocused(false)}
                      type="text"
                      placeholder="name@company.com"
                      className="login-input"
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="login-password" className="text-xs font-medium text-prs-secondary">
                      Password
                    </label>
                    <button
                      type="button"
                      className="text-xs font-medium text-prs-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleForgotPassword}
                      disabled={resetSending || loading}
                    >
                      {resetSending ? "Sending…" : "Forgot Password?"}
                    </button>
                  </div>
                  <div className="relative">
                    <span
                      className={`material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[17px] transition-colors ${
                        passFocused ? "text-prs-primary" : "text-prs-outline"
                      }`}
                    >
                      lock
                    </span>
                    <input
                      id="login-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setPassFocused(true)}
                      onBlur={() => setPassFocused(false)}
                      type="password"
                      placeholder="••••••••"
                      className="login-input"
                      required
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <input
                    id="remember"
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-3.5 w-3.5 cursor-pointer rounded border-prs-outline-variant text-prs-primary focus:ring-prs-primary/20"
                  />
                  <label
                    htmlFor="remember"
                    className="cursor-pointer select-none text-xs text-prs-on-surface-variant"
                  >
                    Remember this device for 30 days
                  </label>
                </div>

                {resetMessage && (
                  <div
                    role="status"
                    className="flex items-start gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-xs text-emerald-900"
                  >
                    <span className="material-symbols-outlined mt-0.5 shrink-0 text-[16px]">mark_email_read</span>
                    <span>{resetMessage}</span>
                  </div>
                )}

                {error && (
                  <div
                    role="alert"
                    className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-800"
                  >
                    <span className="material-symbols-outlined mt-0.5 shrink-0 text-[16px]">error</span>
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-1.5 rounded-md bg-prs-primary-container py-2.5 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:bg-prs-primary active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <span className="material-symbols-outlined animate-spin text-[16px]">
                        progress_activity
                      </span>
                      Authenticating…
                    </>
                  ) : (
                    <>
                      Sign In
                      <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                    </>
                  )}
                </button>
              </form>

              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-prs-outline-variant" />
                </div>
                <div className="relative flex justify-center text-[10px] font-semibold uppercase tracking-wide">
                  <span className="bg-white px-3 text-prs-outline">staff access</span>
                </div>
              </div>

              <p className="text-center text-xs text-prs-on-surface-variant">
                Staff incentive and attendance (PIN from HR):{" "}
                <Link
                  to="/employee-portal"
                  className="font-bold text-prs-primary transition-colors hover:underline"
                >
                  Employee portal
                </Link>
              </p>
              </>
              )}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-center gap-1.5 text-prs-outline">
            <span className="material-symbols-outlined text-[16px]">verified_user</span>
            <span className="text-[10px] font-semibold">Secured by PRS Multi-Factor Encryption</span>
          </div>
        </div>
      </main>

      <footer className="border-t border-prs-outline-variant/50 bg-prs-surface-low px-4 py-3 md:px-8">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-between gap-2 md:flex-row">
          <div className="text-center md:text-left">
            <p className="text-xs font-bold text-prs-primary">PRS Corp</p>
            <p className="text-[11px] text-prs-secondary">
              © {new Date().getFullYear()} PRS Corp. All rights reserved.
            </p>
          </div>
          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            {["Privacy Policy", "Terms of Service", "Cookie Settings", "Contact Support"].map((label) => (
              <button
                key={label}
                type="button"
                className="text-[11px] text-prs-on-surface-variant underline-offset-2 hover:text-prs-primary hover:underline"
                onClick={() => toast(`${label} — contact your administrator.`, { duration: 4000 })}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}
